import { useState, useEffect } from "react";
import { EnhancedBrickOptimizer, LayerData } from "../../utils/brickOptimizer";
import { getLegoColor, normalizeColorIndex } from "../../constants/legoColors";
import { buildDepthLayers } from "../../utils/depthLayers";

interface PixelData {
  pixels: number[][];
  width: number;
  height: number;
}

interface Brick {
  x: number;
  y: number;
  width: number;
  height: number;
  colorIndex: number;
  colorName: string;
}

export const useLayerGeneration = (
  pixelData: PixelData | null,
  optimizationEnabled: boolean,
  onLayersGenerated?: (layers: LayerData[]) => void,
  depthBands = 1,
) => {
  const [layers, setLayers] = useState<LayerData[]>([]);

  useEffect(() => {
    if (!pixelData) return;

    let generatedLayers: LayerData[] = [];

    if (depthBands > 1) {
      // Depth (3D) mode: stacked layers from luminance bands. The flat
      // optimizer is bypassed — each cell is a solid 1x1 column stack.
      generatedLayers = buildDepthLayers(
        pixelData.pixels,
        pixelData.width,
        pixelData.height,
        depthBands,
      );
    } else if (optimizationEnabled) {
      // Use enhanced brick optimization with multi-pass strategy
      const optimizer = new EnhancedBrickOptimizer(
        pixelData.pixels,
        pixelData.width,
        pixelData.height,
      );
      generatedLayers = optimizer.optimize(1); // Single layer for now
    } else {
      // Fallback to simple pixel-by-pixel approach
      const layerMap = new Map<number, Brick[]>();

      for (let y = 0; y < pixelData.height; y++) {
        for (let x = 0; x < pixelData.width; x++) {
          const colorIndex = normalizeColorIndex(pixelData.pixels[y][x]);
          const color = getLegoColor(colorIndex);
          const brightness = (color.r + color.g + color.b) / 3;

          if (brightness > 240 || color.name === "White") continue;

          const brick: Brick = {
            x,
            y,
            width: 1,
            height: 1,
            colorIndex,
            colorName: getLegoColor(colorIndex).name,
          };

          if (!layerMap.has(0)) {
            layerMap.set(0, []);
          }
          layerMap.get(0)!.push(brick);
        }
      }

      generatedLayers = Array.from(layerMap.entries()).map(
        ([layer, bricks]) => ({
          layer,
          bricks: bricks.map((b) => ({
            ...b,
            brickType: {
              id: "1x1",
              name: "1x1 Brick",
              width: 1,
              height: 1,
              description: "Basic 1x1 brick",
              isCommon: true,
            },
            z: 0,
            rotation: 0 as const,
          })),
          brickCount: bricks.length,
          colorUsage: {},
        }),
      );
    }

    // Add color names to optimized bricks
    generatedLayers = generatedLayers.map((layer) => ({
      ...layer,
      bricks: layer.bricks.map((brick) => ({
        ...brick,
        colorIndex: normalizeColorIndex(brick.colorIndex),
        colorName: getLegoColor(brick.colorIndex).name,
      })),
    }));

    setLayers(generatedLayers);
    onLayersGenerated?.(generatedLayers);
  }, [pixelData, optimizationEnabled, onLayersGenerated, depthBands]);

  return layers;
};
